// src/features/services/agent/components/ThreadList.tsx
// ─────────────────────────────────────────────────────────────────────────────
// List of agent threads with status badges and expandable proposal preview.
// PAC Glass surfaces. Data owned by useAgentThreads hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useAgentThreads } from '../hooks/useAgent';
import ProposalPreview from './ProposalPreview';
import type { AgentThread, ConfirmPayload } from '../api/agentApi';

const STATUS_COLORS: Record<string, string> = {
  processing:'bg-purple-900/40 text-purple-400 border-purple-700/40',
  pending:   'bg-amber-900/40 text-amber-400 border-amber-700/40',
  confirmed: 'bg-emerald-900/40 text-emerald-400 border-emerald-700/40',
  rejected:  'bg-neutral-800 text-neutral-400 border-neutral-700/40',
  failed:    'bg-rose-900/40 text-rose-400 border-rose-700/40',
};

const STATUS_LABELS: Record<string, string> = {
  processing:'processing',
  pending:   'pending review',
  confirmed: 'confirmed',
  rejected:  'rejected',
  failed:    'failed',
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] ?? 'bg-neutral-800 text-neutral-400 border-neutral-700/40';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${cls} ${status === 'processing' ? 'animate-pulse' : ''}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function ThreadRow({
  thread,
  isExpanded,
  onToggle,
}: {
  thread: AgentThread;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { confirm, reject } = useAgentThreads();

  const handleConfirm = useCallback((payload?: ConfirmPayload) => {
    confirm.mutate({ key: thread.thread_key, body: payload });
  }, [confirm, thread.thread_key]);

  const handleReject = useCallback(() => {
    reject.mutate(thread.thread_key);
  }, [reject, thread.thread_key]);

  return (
    <div className="space-y-0">
      {/* Collapsed row */}
      <button
        onClick={onToggle}
        className="w-full text-left pac-glass-subtle rounded p-3 border border-white/5
                   hover:border-purple-500/20 transition-colors flex items-center gap-3"
      >
        <StatusBadge status={thread.status} />

        <span className="font-mono text-xs text-purple-400">{thread.thread_key}</span>

        <span className="text-xs text-neutral-300 truncate flex-1">
          {thread.description.slice(0, 80)}{thread.description.length > 80 ? '…' : ''}
        </span>

        <span className="text-[10px] text-neutral-600 whitespace-nowrap">
          {thread.proposal?.skus?.length ?? 0} SKU(s)
        </span>

        {thread.image_count > 0 && (
          <span className="text-[10px] text-neutral-600">{thread.image_count} img</span>
        )}

        <span className="text-[10px] text-neutral-700 whitespace-nowrap">
          {thread.created_at
            ? new Date(thread.created_at).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })
            : ''}
        </span>

        <span className="text-neutral-600 text-xs">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="pl-4 pt-2">
          <ProposalPreview
            thread={thread}
            onConfirm={handleConfirm}
            onReject={handleReject}
            isConfirming={confirm.isPending}
            isRejecting={reject.isPending}
          />
        </div>
      )}
    </div>
  );
}

// ── Main list ────────────────────────────────────────────────────────────

interface ThreadListProps {
  autoExpandKey?: string | null;
}

export default function ThreadList({ autoExpandKey }: ThreadListProps) {
  const { data, isLoading, isError, error } = useAgentThreads({ limit: 50 });
  const [expanded, setExpanded] = useState<string | null>(null);
  const lastAutoRef = React.useRef<string | null>(null);

  // Auto-expand when a new thread is created
  React.useEffect(() => {
    if (autoExpandKey && autoExpandKey !== lastAutoRef.current) {
      lastAutoRef.current = autoExpandKey;
      setExpanded(autoExpandKey);
    }
  }, [autoExpandKey]);

  const toggle = useCallback(
    (key: string) => setExpanded(prev => (prev === key ? null : key)),
    [],
  );

  const threads: AgentThread[] = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500 animate-pulse">Loading agent threads…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-rose-400">{(error as Error)?.message ?? 'Failed to load threads'}</p>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500">
          No agent threads yet. Create one above to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="pac-glass rounded-lg p-5 space-y-3">
      <h2 className="text-sm font-semibold text-neutral-200 uppercase tracking-wider">
        Thread History
      </h2>

      <div className="space-y-2">
        {threads.map(t => (
          <ThreadRow
            key={t.thread_key}
            thread={t}
            isExpanded={expanded === t.thread_key}
            onToggle={() => toggle(t.thread_key)}
          />
        ))}
      </div>
    </div>
  );
}
